/** @jsx jsx */
import { jsx } from '@emotion/core';
import PropTypes from 'prop-types';
import defaultsDeep from 'lodash/defaultsDeep';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import axios from 'axios';
import { withNamespaces } from 'react-i18next';
import { Container, Row, Column } from '../../../../styles/Grid';
import { Breakpoints } from '../../../../styles/Grid/GridSizes';
import Picture from '../../../ui/Picture';
import CtaLink from '../../../ui/CtaLink';
import { types } from '../../../ui/CtaLink/types';
import Modal from '../../../ui/Modal';
import { showElementCss } from '../../../../utils/responsive';
import MediaQueries from '../../../../styles/Grid/MediaQueries';
import {
  CLOUDINARY_BASE_VALUES,
  CLOUDINARY_TEST_IMG_EXIST_TRANSFORMATIONS,
  IMAGE_CONFIG_VALUE,
} from '../../../../utils/constants';
import { SafePureComponent } from '../../../../utils/SafePureComponent';
import LocationSlider from './LocationSlider';
import {
  containerMarginCss,
  galleryWrapperCss,
  gallerySliderCss,
  pictureCss,
  imageCss,
  viewMoreWrapperCss,
  viewMoreTextCss,
  buttonResetCss,
  closeButtonCss,
} from './index.styles';
import { defaultData } from '../data';

const baseImgValues = {
  [Breakpoints.xs]: {
    ...CLOUDINARY_BASE_VALUES,
    height: 197,
  },
  [Breakpoints.sm]: {
    ...CLOUDINARY_BASE_VALUES,
    height: 197,
  },
  [Breakpoints.md]: {
    ...CLOUDINARY_BASE_VALUES,
    height: 308,
  },
};

const getImageSize = {
  1: {
    [Breakpoints.xs]: {
      ...CLOUDINARY_BASE_VALUES,
      aspectRatio: '9:5',
      width: 351,
    },
    [Breakpoints.sm]: {
      ...CLOUDINARY_BASE_VALUES,
      aspectRatio: '9:5',
      width: 351,
    },
    [Breakpoints.md]: {
      ...CLOUDINARY_BASE_VALUES,
      aspectRatio: '9:5',
      width: 548,
    },
    [Breakpoints.lg]: {
      ...CLOUDINARY_BASE_VALUES,
      aspectRatio: '9:5',
      width: 584,
    },
    [Breakpoints.xl]: {
      ...CLOUDINARY_BASE_VALUES,
      aspectRatio: '9:5',
      width: 702,
    },
  },
  2: {
    [Breakpoints.xs]: {
      ...CLOUDINARY_BASE_VALUES,
      gravity: 'auto:subject',
      aspectRatio: '7:10',
      height: 197,
      width: 142,
    },
    [Breakpoints.sm]: {
      ...CLOUDINARY_BASE_VALUES,
      gravity: 'auto:subject',
      aspectRatio: '7:10',
      height: 197,
      width: 142,
    },
    [Breakpoints.md]: {
      ...CLOUDINARY_BASE_VALUES,
      gravity: 'auto:subject',
      aspectRatio: '7:10',
      height: 308,
      width: 217,
    },
    [Breakpoints.lg]: {
      ...CLOUDINARY_BASE_VALUES,
      gravity: 'auto:subject',
      aspectRatio: '7:10',
      height: 246,
      width: 180,
    },
    [Breakpoints.xl]: {
      ...CLOUDINARY_BASE_VALUES,
      gravity: 'auto:subject',
      aspectRatio: '7:10',
      height: 296,
      width: 217,
    },
  },
  3: {
    [Breakpoints.xs]: {
      ...CLOUDINARY_BASE_VALUES,
      aspectRatio: '20:11',
      width: 351,
    },
    [Breakpoints.sm]: {
      ...CLOUDINARY_BASE_VALUES,
      aspectRatio: '20:11',
      width: 351,
    },
    [Breakpoints.md]: {
      ...CLOUDINARY_BASE_VALUES,
      aspectRatio: '20:11',
      width: 556,
    },
    [Breakpoints.lg]: {
      ...CLOUDINARY_BASE_VALUES,
      aspectRatio: '20:11',
      width: 352,
    },
    [Breakpoints.xl]: {
      ...CLOUDINARY_BASE_VALUES,
      aspectRatio: '20:11',
      width: 404,
    },
  },
  4: {
    ...baseImgValues,
  },
  5: {
    ...baseImgValues,
  },
  6: {
    ...baseImgValues,
  },
  7: {
    ...baseImgValues,
  },
  8: {
    ...baseImgValues,
  },
  9: {
    ...baseImgValues,
  },
  10: {
    ...baseImgValues,
  },
  11: {
    ...baseImgValues,
  },
};

export const getSlideSrc = (locationID, imageNumber) => {
  return `https://assets.iwgplc.com/image/upload/${IMAGE_CONFIG_VALUE}/CentreImagery/${locationID}/${locationID}_${imageNumber}.jpg`;
};

// For chekup if img exists, just need any transformation params, NO untransformed imgs requests
export const getCheckupImgRequestSrc = (locationID, imageNumber) => {
  return `https://assets.iwgplc.com/image/upload/${CLOUDINARY_TEST_IMG_EXIST_TRANSFORMATIONS}/CentreImagery/${locationID}/${locationID}_${imageNumber}.jpg`;
};

class ImageGallery extends SafePureComponent {
  fields = defaultsDeep(this.props.fields, defaultData);
  defaultImages = [
    this.fields.HeroImage1,
    this.fields.HeroImage2,
    this.fields.HeroImage3,
  ];
  state = {
    isModalOpen: false,
    locationID: get(this.props.fields, 'ProtonId.value'),
    slides: [
      {
        number: 5,
        keyValue: 'MainImage',
      },
      {
        number: 2,
        keyValue: 'BottomRight',
      },
      {
        number: 4,
        keyValue: 'BottomLeft',
      },
      {
        number: 6,
        keyValue: 'OfficeInternal',
      },
      {
        number: 7,
        keyValue: 'SharedOffice',
      },
      {
        number: 8,
        keyValue: 'MeetingRoom1',
      },
      {
        number: 9,
        keyValue: 'MeetingRoom2',
      },
      {
        number: 3,
        keyValue: 'BoardRoom',
      },
      {
        number: 1,
        keyValue: 'BuildingExternal',
      },
      {
        number: 10,
        keyValue: 'BuildingFeatureExternal',
      },
      {
        number: 11,
        keyValue: 'BuildingPlacementExternal',
      },
    ],
  };

  testImage = (src, number) => {
    // `head` to avoid a lot of data transferred
    axios.head(src).catch(() => {
      this.setState({
        slides: this.state.slides.filter(slide => slide.number !== number),
      });
    });
  };

  // TODO: available images indexes to get from backend
  checkAllSlidesforErrors = () => {
    const { locationID, slides } = this.state;

    slides.forEach(slide => {
      const { number } = slide;

      this.testImage(getCheckupImgRequestSrc(locationID, number), number);
    });
  };

  componentDidMount() {
    this.checkAllSlidesforErrors();
  }

  closeGallery = () => {
    this.setState({ isModalOpen: false });
  };

  openGallery = imageNumber => {
    const { slides } = this.state;
    !isEmpty(slides) &&
      this.setState({ slideToOpen: imageNumber, isModalOpen: true });
  };

  renderModal = () => {
    const { slides, isModalOpen, slideToOpen } = this.state;

    return (
      <Modal
        isOpen={isModalOpen}
        onRequestClose={this.closeGallery}
        customCloseButtonCss={closeButtonCss}
      >
        <LocationSlider
          {...this.props}
          slideToOpen={slideToOpen}
          slides={slides}
        />
      </Modal>
    );
  };

  renderDesktopImages = (t, { ProtonName }) => {
    const { slides } = this.state;

    return this.defaultImages.map((image, index) => {
      const imageNumber = index + 1;
      const { FallBackImage } = image;
      const getfallBackAlt = `${t(get(slides, `[${index}].keyValue`))} ${
        ProtonName.value
      }`;

      return (
        <Column key={index} noGutter>
          <button onClick={() => this.openGallery(index)} css={buttonResetCss}>
            <Picture
              image={image}
              fallBack={FallBackImage}
              fallbackAlt={getfallBackAlt}
              imageSizes={getImageSize[imageNumber]}
              pictureCss={pictureCss}
              imageCss={imageCss}
              pictureClassName={`image-${index}`}
              isClientLink
              instantLoad
            />
          </button>
          {/* Workaround to asign the CTA to the third image. */}
          {index === 2 && (
            <div
              css={[showElementCss(MediaQueries.lgAndUp), viewMoreWrapperCss]}
            >
              <CtaLink
                linkCustomCss={viewMoreTextCss}
                text={t('ViewGalleryCTA')}
                type={types.secondary}
                onClick={this.openGallery}
              />
            </div>
          )}
        </Column>
      );
    });
  };

  renderMobileSlideImages = (t, { ProtonName }) => {
    const { locationID, slides } = this.state;
    if (isEmpty(slides)) {
      return this.defaultImages.map((image, index) => {
        const imageNumber = index + 1;
        const { FallBackImage } = image;
        const getfallBackAlt = `${t(get(slides, `[${index}].keyValue`))} ${
          ProtonName.value
        }`;

        return (
          <Column key={index} noGutter>
            <Picture
              image={image}
              fallBack={FallBackImage}
              fallbackAlt={getfallBackAlt}
              imageSizes={getImageSize[imageNumber]}
              pictureCss={pictureCss}
              pictureClassName={`image-${index}`}
              isClientLink
              instantLoad
            />
          </Column>
        );
      });
    }

    return slides.map((slide, index) => {
      const imageNumber = index + 1;
      const imageSrc = {
        value: { src: getSlideSrc(locationID, slide.number) },
      };
      const imageAlt = `${t(slide.keyValue)} ${ProtonName.value}`;

      return (
        <Column key={index} noGutter>
          <Picture
            image={imageSrc}
            fallbackAlt={imageAlt}
            imageSizes={getImageSize[imageNumber]}
            pictureCss={pictureCss}
            imageClassName={`image-${index}`}
            isClientLink
            instantLoad
          />
        </Column>
      );
    });
  };

  safeRender() {
    const { props, fields } = this;
    const { t } = props;

    return (
      <div css={containerMarginCss}>
        <Container css={galleryWrapperCss}>
          <Row
            css={[
              gallerySliderCss,
              showElementCss(
                [MediaQueries.xs, MediaQueries.sm, MediaQueries.md],
                'flex',
              ),
            ]}
          >
            {this.renderMobileSlideImages(t, fields)}
          </Row>
          <Row
            css={[
              gallerySliderCss,
              showElementCss(MediaQueries.lgAndUp, 'flex'),
            ]}
          >
            {this.renderDesktopImages(t, fields)}
          </Row>
        </Container>
        {this.renderModal()}
      </div>
    );
  }
}

ImageGallery.displayName = 'LocationHeader/ImageGallery';

ImageGallery.defaultProps = { fields: defaultData, t: () => {} };

const ImageType = {
  value: {
    alt: PropTypes.string,
    height: PropTypes.string,
    src: PropTypes.string,
    width: PropTypes.string,
  },
};

ImageGallery.propTypes = {
  props: PropTypes.shape({
    HeroImage1: PropTypes.shape({
      ImageType,
    }),
    HeroImage2: PropTypes.shape({
      ImageType,
    }),
    HeroImage3: PropTypes.shape({
      ImageType,
    }),
  }),
  t: PropTypes.func,
};

export default withNamespaces()(ImageGallery);
